import {
  FormControl,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { useFlag } from '@unleash/proxy-client-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'urql';
import { useTranslation } from 'react-i18next';
import { ReactComponent as SparkelCopilotIcon } from '../../assets/icons/sparkel-copilot.svg';

import { AskCopilotDocument, CopilotAnswer } from '../../gql/graphql';
import { useLocalStorage } from '../../hooks/local-storage';
import { EventName, useMixpanel } from '../../services/mixpanel';
import { useBreadcrumb } from './BreadcrumbProvider';
import SparkelIcon from './icon/SparkelIcon';

type SparkelCopilotProps = {
  projectId: string;
  onCopilotResult: (result: CopilotAnswer['result'], request: string) => void;
};

const SparkelCopilotLoadingPhrases = [
  'Engaging the AI gears',
  'Architecting the AI-BIM fusion',
  'Activating the neural network',
  'Commencing AI-guided BIM model interrogation',
  'Initiating the AI-assisted BIM element extraction process',
  'Utilizing cutting-edge AI technology',
  'Harnessing the power of AI for precise BIM element selection.',
  'Activating the BIM-bot army to identify model elements',
  'Letting the AI circus monkeys loose on the BIM model',
  'Initiating the BIM element selection dance-off with the AI',
  'Summoning the BIM element genie',
  'Letting the AI BIM-ninjas do their element selection magic',
  'Revolutionizing BIM model selection with cutting-edge software',
  'Selecting BIM elements with the speed and accuracy of advanced robotics',
  'Advancing BIM modeling with high-tech element recognition techniques',
  'Streamlining the BIM element selection process with state-of-the-art technology',
  'Employing sophisticated algorithms for efficient BIM model element selection',
  'Combining data analytics and 3D modeling for intelligent BIM element identification',
  'Enhancing BIM model inspection with automated element recognition and tagging',
  'Empowering construction professionals with advanced tools for BIM element selection.',
];

export const COPILOT_LOCAL_STORAGE_VAR = 'display-copilot-for-user';

// eslint-disable-next-line complexity
export default function SparkelCopilot({
  projectId,
  onCopilotResult,
}: SparkelCopilotProps) {
  const enableCopilot = useFlag('enable-copilot');

  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting: isAskingCopilot },
    reset,
    watch,
  } = useForm<{
    query: string;
  }>({
    defaultValues: {
      query: '',
    },
  });
  const currentUserQuery = watch('query');

  const [askCopilotResult, askCopilot] = useMutation(AskCopilotDocument);

  useEffect(() => {
    if (askCopilotResult.data?.askCopilot && !askCopilotResult.fetching) {
      reset({
        query: '',
      });
    }
  }, [askCopilotResult.data?.askCopilot, askCopilotResult.fetching, reset]);

  const [randomLoadingPhrase, setRandomLoadingPhrase] = useState(
    SparkelCopilotLoadingPhrases[
      Math.floor(Math.random() * SparkelCopilotLoadingPhrases.length)
    ]
  );

  const { projectName } = useBreadcrumb();

  const { t } = useTranslation('project');

  const placeholder = t('sparkel-copilot.placeholder');

  const { trackEvent } = useMixpanel();

  const onSubmitAi = handleSubmit(async ({ query }) => {
    const { error, data: copilotData } = await askCopilot({ query, projectId });
    if (error) {
      toast({
        title: 'Sparkel copilot was unable to process your request',
        description: error.message,
        status: 'error',
        position: 'top',
        duration: 5000,
        isClosable: true,
      });
      console.error(error);
      return;
    }
    if (copilotData?.askCopilot?.result) {
      console.log(copilotData.askCopilot.result);
      onCopilotResult(copilotData.askCopilot.result, currentUserQuery);

      trackEvent(EventName.CopilotPrompted, {
        'Project Name': projectName,
        'Project ID': projectId,
        Prompt: query,
      });
    }
    setRandomLoadingPhrase(
      SparkelCopilotLoadingPhrases[
        Math.floor(Math.random() * SparkelCopilotLoadingPhrases.length)
      ]
    );
  });

  const [displayCopilotForUser] = useLocalStorage(
    COPILOT_LOCAL_STORAGE_VAR,
    true
  );

  if (!enableCopilot || !displayCopilotForUser) {
    return null;
  }

  return (
    <>
      <HStack>
        <form onSubmit={onSubmitAi}>
          <FormControl isDisabled={isAskingCopilot}>
            <InputGroup size={'sm'}>
              <InputLeftElement>
                <SparkelIcon as={SparkelCopilotIcon} color="teal.400" />
              </InputLeftElement>
              <Input
                {...register('query')}
                autoComplete="off"
                background="gray.800"
                borderColor="gray.700"
                _placeholder={{ color: 'gray.500' }}
                color="gray.50"
                isRequired
                width="md"
                placeholder={placeholder}
              />
              <InputRightElement>
                <Tooltip
                  label={
                    isAskingCopilot
                      ? t('sparkel-copilot.tooltip.waiting')
                      : t('sparkel-copilot.tooltip.send')
                  }
                  openDelay={400}
                >
                  {isAskingCopilot ? (
                    <Spinner size={'sm'} color="teal.200" />
                  ) : (
                    <IconButton
                      aria-label="Send prompt"
                      onClick={onSubmitAi}
                      size="xs"
                      colorScheme={isAskingCopilot ? 'gray' : 'brand'}
                      isRound
                      display={currentUserQuery === '' ? 'none' : undefined}
                      isDisabled={isAskingCopilot || currentUserQuery === ''}
                      icon={<SparkelIcon icon={faArrowUp} color={'inherit'} />}
                    />
                  )}
                </Tooltip>
              </InputRightElement>
            </InputGroup>
          </FormControl>
        </form>
      </HStack>
    </>
  );
}
